import { PlusOutlined, CheckCircleOutlined, BankOutlined } from "@ant-design/icons";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
  ADVANCED_QUERY,
} from "../components/AdvancedSearchModal";
import * as Sentry from "@sentry/browser";
import { PageToolbar } from "../components/PageToolbar";
import {
  hasActiveFilters,
  clearAllFilters,
  parseQueryParams,
  buildQueryString,
  handleFieldRemove,
} from "../components/TableFilters";
import {
  Button,
  Tag,
  Table,
  message,
  Row,
  Col,
} from "antd";
import debounce from "lodash.debounce";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { LoanRequest, RowSelection } from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
  generateNameFromCustomer,
  getStateColor,
  request,
  fetchEnumOptions,
  showTotalPagination,
} from "../helpers/utilities";
import {
  SEARCH_LOAN_REQUESTS,
  GET_LOAN_REQUEST_SORT_FIELDS,
  GET_PROVIDERS,
} from "../gql/loans";
import ModalCreator from "../components/ModalCreator";
import { NewLoanRequest } from "./NewLoanRequest";
import ExportData from "../components/ExportData";
import { LOAN_REQUEST_STATES } from "../helpers/constants";
import moment from "moment";
import { AxiosResponse } from "axios";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";

interface State {
  loanRequests: LoanRequest[];
  page: number;
  loanRequestsCount: number;
  isLoading: boolean;
  showNewLoanRequestModal: boolean;
  selectedIdsToExport: React.Key[];
  selectedStates: string[];
  selectedProviders: string[];
  pageSize: number;
  q: string;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string; }>;
  providerOptions: Array<{ value: string; label: string; }>;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export const loanRequestsColumns = [
  {
    title: "Request Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render(createdAt: string): JSX.Element {
      return (
        <span>
          {createdAt
            ? moment(createdAt, "YYYY-MM-DD HH:mm:ss").format("llll")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Customer",
    dataIndex: "customer",
    key: "customer",
    align: "left" as const,
    render(customer: any, record: LoanRequest): JSX.Element {
      return (
        <NavLink to={`/loan-requests/${record.id}`}>
          {generateNameFromCustomer(record.customer)}
        </NavLink>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "state",
    key: "state",
    render(state: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(state)}
          style={{ textTransform: "capitalize" }}
        >
          {state?.replace(/_/g, " ") || "N/A"}
        </Tag>
      );
    },
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    render(source: string): JSX.Element {
      return <span>{capitalizeFirstLetter(source)}</span>;
    },
  },
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    render(product: any): JSX.Element {
      return <span>{product?.name || "N/A"}</span>;
    },
  },
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    render(provider: any): JSX.Element {
      return provider ? (
        <NavLink to={`/providers/${provider.id}`}>{provider.name}</NavLink>
      ) : (
        <span>N/A</span>
      );
    },
  },
  {
    title: "Loan Type",
    dataIndex: "loanType",
    key: "loanType",
    render(text: string): JSX.Element {
      return (
        <span style={{ textTransform: "capitalize" }}>
          {(text && formatSnakeCase(text)) || "N/A"}
        </span>
      );
    },
  },
  {
    title: "Tenor (Months)",
    dataIndex: "loanTenureMonths",
    key: "loanTenureMonths",
    render(loanTenureMonths: string): JSX.Element {
      return <span>{loanTenureMonths || "N/A"}</span>;
    },
  },
  {
    title: "Amount Requested",
    dataIndex: "loanAmountRequested",
    key: "loanAmountRequested",
    align: "right" as const,
    render(amount: any): JSX.Element {
      const amountValue =
        typeof amount === "object" && amount?.parsedValue
          ? amount.parsedValue
          : amount;
      return <span>{amountValue ? formatter.format(amountValue) : "N/A"}</span>;
    },
  },
];

export function LoanRequestTableFactory(
  data: LoanRequest[],
  count: number,
  page: number,
  nextPage: (page: number, pageSize?: number) => void,
  rowSelection: RowSelection,
  pageSize: number,
  loading?: boolean,
): JSX.Element {
  const tempData: LoanRequest[] = data.map((loanRequest: LoanRequest): any => {
    return { ...loanRequest, key: loanRequest.id };
  });
  return (
    <Table
      scroll={{ x: 500 }}
      columns={loanRequestsColumns}
      dataSource={tempData}
      loading={loading}
      pagination={{
        current: page,
        pageSize,
        total: count,
        onChange: nextPage,
        showSizeChanger: true,
        showTotal: showTotalPagination,
      }}
      rowSelection={rowSelection}
    />
  );
}

export default class LoanRequests extends React.Component<
  RouteComponentProps,
  State
> {
  private _isMounted = false;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      loanRequests: [],
      page: 1,
      loanRequestsCount: 0,
      showNewLoanRequestModal: false,
      selectedIdsToExport: [],
      selectedStates: [],
      selectedProviders: [],
      pageSize: 25,
      q: "",
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      providerOptions: [],
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };

    this.redirectToNewLoanRequestPage =
      this.redirectToNewLoanRequestPage.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.handleStateFilter = this.handleStateFilter.bind(this);
    this.handleProviderFilter = this.handleProviderFilter.bind(this);
  }

  toggleAdvancedSearchModel = (): void => {
    this.setState((prevState) => ({
      showAdvancedSearchModal: !prevState.showAdvancedSearchModal,
    }));
  };

  handleOnAdvancedSearchError = (error: string): void => {
    message.error(error);
    this.setState({ isLoading: false });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ): void => {
    this.setState({
      isLoading: false,
      loanRequests: data.results as LoanRequest[],
      loanRequestsCount: data.count,
      selectedFieldsToDisplay: fields,
      removedField: undefined,
    });
  };

  handleOnFieldRemove = (field: IField): void => {
    handleFieldRemove(this, field, "loanRequest");
  };

  handleStateFilter(newSelectedStates: string[]): void {
    this.setState({ selectedStates: newSelectedStates, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  }

  handleProviderFilter(newSelectedProviders: string[]): void {
    this.setState({ selectedProviders: newSelectedProviders, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  }

  triggerImmediateSearch = async (): Promise<void> => {
    if (!this._isMounted) return;

    const {
      q,
      page,
      pageSize,
      selectedStates,
      selectedProviders,
      sortField,
      sortDirection,
      selectedFieldsToDisplay,
    } = this.state;

    this.setState({ isLoading: true });

    try {
      let results = [];
      let count = 0;

      if (selectedFieldsToDisplay.length > 0) {
        const fields = selectedFieldsToDisplay.map((f: IField) => ({
          field: f.name,
          operator: f.operator,
          values: f.hasBetween ? [f.value, f.betweenValue] : [f.value],
        }));

        const r: AxiosResponse = await request({
          query: ADVANCED_QUERY,
          variables: {
            model: "loanRequest",
            fields,
            page,
            numberPerPage: pageSize,
          },
        });

        const advancedData = r.data?.data?.advancedQuery || {};
        results = advancedData.results || [];
        count = advancedData.count || 0;
      } else {
        const r: AxiosResponse = await request({
          query: SEARCH_LOAN_REQUESTS,
          variables: {
            q,
            page,
            numberPerPage: pageSize,
            states: selectedStates.length > 0 ? selectedStates : null,
            providerIds: selectedProviders.length > 0 ? selectedProviders : null,
            orderBy: {
              field: sortField,
              direction: sortDirection,
            },
          },
        });

        const searchData = r.data?.data?.loanRequestSearch;

        if (!searchData) {
          if (this._isMounted) message.error("Failed To Load Loan Requests");
          return;
        }

        results = searchData.results || [];
        count = searchData.count;
      }

      if (this._isMounted) {
        this.syncBrowserQueryString();
        this.setState({
          loanRequests: results,
          loanRequestsCount: count,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      if (this._isMounted) this.setState({ isLoading: false });
    }
  };
  syncBrowserQueryString = (): void => {
    const queryStringResult = buildQueryString(this.state as any, {
      includeSort: true,
      includeProviders: true,
    });
    this.props.history.replace(queryStringResult);
  };

  handleSortChange = (field: string, direction: string): void => {
    this.setState({ sortField: field, sortDirection: direction, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value, page: 1 });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  fetchSortFieldOptions = async (): Promise<void> => {
    const sortFieldOptions = await fetchEnumOptions(
      GET_LOAN_REQUEST_SORT_FIELDS,
      {
        labelFormatter: (name) =>
          formatSnakeCase(capitalizeFirstLetter(name)),
        errorMsg: "Failed to fetch sort field options",
      },
    );

    if (this._isMounted) {
      this.setState({ sortFieldOptions });
    }
  };

  fetchProviderOptions = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: GET_PROVIDERS,
        variables: { perPage: "100" },
      });

      const providers = response.data?.data?.getProviders || [];
      const providerOptions = providers.map((p: any) => ({
        value: p.id,
        label: p.name,
      }));

      if (this._isMounted) {
        this.setState({ providerOptions });
      }
    } catch (error) {
      message.error(`Failed to fetch provider options - ${generateDisplayErrorMessage(error)}`)
      Sentry.captureException(error);
    }
  };

  mergeWithLocalState = (callback?: () => void): void => {
    const parsed = parseQueryParams({
      includeSort: true,
      includeProviders: true,
    });

    this.setState(
      {
        q: parsed.q || "",
        page: parsed.page || 1,
        selectedStates: parsed.selectedStates || [],
        selectedProviders: parsed.selectedProviders || [],
        sortField: parsed.sortField || "CREATED_AT",
        sortDirection: parsed.sortDirection || "DESC",
      },
      callback,
    );
  };

  redirectToNewLoanRequestPage(): void {
    this.props.history.push("/loan-requests/new");
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    const savedFields = JSON.parse(localStorage.getItem("loanRequest") || "[]");
    this.setState({ selectedFieldsToDisplay: savedFields });

    await Promise.all([
      this.fetchSortFieldOptions(),
      this.fetchProviderOptions(),
    ]);
    this.mergeWithLocalState(() => {
      this.triggerImmediateSearch();
    });
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  async nextPage(page: number, pageSize?: number): Promise<void> {
    if (!this._isMounted) return;

    const ps = typeof pageSize === "number" ? pageSize : this.state.pageSize;

    this.setState(
      { isLoading: true, page, pageSize: ps },
      async () => {
        await this.triggerImmediateSearch();
        this.setState({ isLoading: false });
      },
    );
  }

  toggleCreateLoanRequestModal(): void {
    this.setState((prevState) => ({
      showNewLoanRequestModal: !prevState.showNewLoanRequestModal,
    }));
  }

  onSelectChange = (newSelectedIdsToExport: React.Key[]) => {
    this.setState({
      selectedIdsToExport: newSelectedIdsToExport,
    });
  };

  render(): JSX.Element | null {
    const {
      loanRequests,
      isLoading,
      loanRequestsCount,
      page,
      showNewLoanRequestModal,
      selectedIdsToExport,
      selectedStates,
      selectedProviders,
      pageSize,
      q,
      showAdvancedSearchModal,
      selectedFieldsToDisplay,
      removedField,
    } = this.state;

    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    const hasFilters = hasActiveFilters(this.state as any, {
      includeSort: true,
      includeProviders: true,
    });

    return (
      <React.Fragment>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={showAdvancedSearchModal}
          model="loanRequest"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
          activeFields={selectedFieldsToDisplay}
        />
        <Card>
          <div className="departments-table-container">
            <PageToolbar
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                searchValue={q}
                onSearchChange={this.handleSearchInput}
                searchPlaceholder="Search by petra id, state, provider, product..."
                actions={
                  <ExportData
                    model={"loanRequest"}
                    dataArray={loanRequests}
                    customIds={selectedIdsToExport}
                  />
                }
                primaryAction={
                  <EntitledSection
                    entitlement={entitlements.LoanRequests?.create}
                  >
                    <Button
                      className="base-button"
                      size="large"
                      onClick={this.redirectToNewLoanRequestPage}
                      data-cy="test-new-loan-button"
                    >
                      <PlusOutlined />
                      Create Loan Request
                    </Button>
                  </EntitledSection>
                }
                totalCount={loanRequestsCount}
                currentDataCount={loanRequests.length}
                page={page}
                pageSize={pageSize}
                selectedCount={selectedIdsToExport.length}
                filters={
                  <PageToolbar.Filters
                    isFiltering={hasFilters}
                    onClear={() => clearAllFilters(this, "loanRequest")}
                    configs={[
                      {
                        label: "Status",
                        options: LOAN_REQUEST_STATES,
                        selectedValues: selectedStates,
                        onChange: this.handleStateFilter,
                        width: 180,
                        icon: <CheckCircleOutlined />,
                      },
                      {
                        label: "Provider",
                        options: this.state.providerOptions,
                        selectedValues: selectedProviders,
                        onChange: this.handleProviderFilter,
                        width: 220,
                        icon: <BankOutlined />,
                      },
                    ]}
                    sort={{
                      field: this.state.sortField,
                      direction: this.state.sortDirection,
                      options: this.state.sortFieldOptions,
                      onChange: this.handleSortChange,
                    }}
                  />
                }
                modelName="loan requests"
              />

              {LoanRequestTableFactory(
                loanRequests,
                loanRequestsCount,
                page,
                this.nextPage,
                rowSelection,
                pageSize,
                isLoading,
              )}

              {showNewLoanRequestModal &&
                ModalCreator({
                  title: "Create Loan Request",
                  showModal: showNewLoanRequestModal,
                  showFooter: false,
                  onOkay: this.toggleCreateLoanRequestModal,
                  toggleModal: this.toggleCreateLoanRequestModal,
                  inscription: (
                    <>
                      <NewLoanRequest
                        onSuccess={() => {
                          this.toggleCreateLoanRequestModal();
                          this.triggerImmediateSearch();
                        }}
                      />
                    </>
                  ),
                })}
            </div>
          </Card>
      </React.Fragment>
    );
  }
}
